/**
 * AP2·P3-4 (Run C) — KI-Ordner settings dialog. Opened from a folder's context
 * menu in "Meine Dateien". Sets the KI-Ordner type (free | ws_bound → the
 * workspace guardrails apply and compose on top) and the folder-level system
 * prompt. Saving the prompt surfaces the non-blocking consistency warnings (B3).
 */

import { useEffect, useState } from "react";

import { ryfeApi } from "../api";

import "./ryfe.scss";

import type { KiWarning } from "../api";

export const KiFolderDialog = ({
  ws,
  folderId,
  folderName,
  initialType = "free",
  initialIsKiOrdner = false,
  onClose,
}: {
  ws: string;
  folderId: string;
  folderName: string;
  initialType?: "ws_bound" | "free";
  initialIsKiOrdner?: boolean;
  onClose: () => void;
}) => {
  const [type, setType] = useState<"ws_bound" | "free">(initialType);
  // AP2 Phase 5 (v1.9) — mark this folder as a KI-Ordner (subtype). Only marked
  // folders appear in the KI-Chat picker. Reflects the current state; check +
  // save to mark, uncheck + save to un-mark.
  const [isKi, setIsKi] = useState<boolean>(initialIsKiOrdner);
  const [prompt, setPrompt] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [warnings, setWarnings] = useState<KiWarning[]>([]);

  useEffect(() => {
    let cancelled = false;
    ryfeApi
      .getKiPrompt(ws, "folder", folderId)
      .then((r) => {
        if (!cancelled) {
          setPrompt(r.ki_system_prompt || "");
          setLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [ws, folderId]);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      await ryfeApi.setKiFolderConfig(ws, {
        ki_ordner_id: folderId,
        ki_ordner_type: type,
        is_ki_ordner: isKi,
      });
      const r = await ryfeApi.setKiPrompt(ws, {
        level: "folder",
        ref_id: folderId,
        ki_system_prompt: prompt,
        context: { ki_ordner_id: folderId },
      });
      setWarnings(r.warnings || []);
      setSaved(true);
    } catch {
      setError("Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ryfe-modal" onClick={onClose}>
      <div className="ryfe-modal__card" onClick={(e) => e.stopPropagation()}>
        <h2 className="ryfe-panel__title">KI-Ordner · {folderName}</h2>

        <label
          className="ryfe-field"
          style={{ flexDirection: "row", alignItems: "center", gap: "0.5rem" }}
        >
          <input
            type="checkbox"
            checked={isKi}
            disabled={saving}
            data-testid="ki-folder-mark"
            onChange={(e) => setIsKi(e.target.checked)}
          />
          <span>Als KI-Ordner markieren (erscheint dann im KI-Chat)</span>
        </label>

        <label className="ryfe-field">
          <span>Ordner-Typ</span>
          <select
            className="ryfe-field"
            value={type}
            disabled={saving}
            data-testid="ki-folder-type"
            onChange={(e) => setType(e.target.value as "ws_bound" | "free")}
          >
            <option value="free">Frei (keine Workspace-Guardrails)</option>
            <option value="ws_bound">
              Workspace-gebunden (Guardrails gelten)
            </option>
          </select>
        </label>
        {type === "ws_bound" ? (
          <p className="ryfe-muted" style={{ padding: 0 }}>
            Bindet an diesen Workspace — die Workspace-Guardrails stehen im
            komponierten Prompt ganz oben.
          </p>
        ) : null}

        <label className="ryfe-field">
          <span>Ordner-Systemprompt</span>
          <textarea
            className="ryfe-field"
            rows={5}
            value={prompt}
            disabled={!loaded || saving}
            placeholder="Systemprompt für diesen KI-Ordner …"
            data-testid="ki-folder-prompt"
            onChange={(e) => setPrompt(e.target.value)}
          />
        </label>

        {warnings.length > 0 ? (
          <div className="ryfe-notice" data-testid="ki-folder-warnings">
            <strong>Mögliche Widersprüche</strong> (nicht blockierend):
            <ul style={{ margin: "0.4rem 0 0" }}>
              {warnings.map((w, i) => (
                <li key={i}>
                  <code>{w.level}</code> · {w.severity}: {w.summary}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {error ? <div className="ryfe-auth__error">{error}</div> : null}
        {saved && !warnings.length ? (
          <div className="ryfe-muted" style={{ padding: 0 }}>
            Gespeichert.
          </div>
        ) : null}

        <div className="ryfe-modal__actions">
          <button className="ryfe-btn ryfe-btn--ghost" onClick={onClose}>
            Schließen
          </button>
          <button
            className="ryfe-btn ryfe-btn--primary"
            disabled={!loaded || saving}
            onClick={save}
          >
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
};
